<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'
import { getRootDomainAndFull, isValidUrl } from '@thecompaniesapi/url-helpers'
import { UInputMenu } from '#components'

const props = defineProps({
  domainsToExclude: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  modelValue: {
    default: '',
    type: String,
  },
  searchFields: {
    default: () => segmentation.attributesWeCanSearch,
    type: Array as PropType<SegmentationAttributeWeCanSearch[]>,
  },
  searchSize: {
    default: 10,
    type: Number,
  },
  size: {
    default: 'sm',
    type: String as PropType<UInputSize>,
  },
})

const emit = defineEmits<{
  'selected': [domain: string]
  'update:modelValue': [domain: string]
}>()

const { t } = useI18n()
const api = useApi()

const currentTimestamp = ref<number>()
const inputMenuRef = ref<InstanceType<typeof UInputMenu>>()
const searching = ref(false)
const searchQuery = defineModel<string>()
const searchQueryProcessed = ref('')
const companies = ref<CompanyV2[]>([])

const options = computed(() => {
  return (companies.value || [])
    .filter((suggestion: UInputMenuOption) => {
      return !props.domainsToExclude.includes(`${suggestion.value}`)
    })
    .map((company: CompanyV2): UInputMenuOption => {
      const domain = company.domain?.domain || ''

      return {
        avatar: {
          placeholder: getLogo(domain, 'companies'),
          src: getLogo(domain, 'companies'),
        },
        domain,
        label: domain,
        name: company.about?.name,
        value: domain,
      }
    })
})

const searchAttributes = computed(() => {
  const searchAttributes = []

  if (props.searchFields.includes('domain.domain')) {
    searchAttributes.push('domain')
  }

  if (props.searchFields.includes('about.name')) {
    searchAttributes.push('name')
  }

  return searchAttributes
})

watch(
  () => searchQuery.value,
  () => searchCompanies(Date.now()),
  { immediate: true },
)

async function searchCompanies(timestamp: number) {
  currentTimestamp.value = timestamp
  searching.value = true

  // Clean search query
  searchQuery.value = searchQuery.value?.toLowerCase().trim() || ''

  if (isValidUrl(searchQuery.value)) {
    searchQuery.value = getRootDomainAndFull(searchQuery.value).full
  }

  try {
    const response = await api.companies.fetch({
      domainsToExclude: props.domainsToExclude.join(','),
      search: searchQuery.value,
      searchFields: props.searchFields,
      simplified: true,
      size: props.searchSize,
    })

    if (currentTimestamp.value === timestamp) {
      companies.value = response.data.companies
      searchQueryProcessed.value = searchQuery.value
    }
  }
  catch {
    companies.value = []
  }

  searching.value = false
}

function onOpenSuggestions() {
  ;(inputMenuRef.value as any)?.trigger?.$el.click()
}

function onSuggestionSelected(suggestion: UBadge) {
  emit('selected', suggestion.value!)
  emit('update:modelValue', suggestion.value!)
}
</script>

<template>
  <UInputMenu
    ref="inputMenuRef"
    v-model:query="searchQuery"
    :icon="getIcon('search')"
    :options="options"
    :placeholder="t('placeholder')"
    :search-attributes="searchAttributes"
    :size="size"
    @change="onSuggestionSelected"
    @focus="onOpenSuggestions"
  >
    <template #empty>
      <span>{{ t('noCompaniesFound') }}</span>
    </template>

    <template #trailing>
      <div
        class="flex size-full items-center justify-center"
        @click="onOpenSuggestions"
      >
        <UIcon
          class="size-4 text-gray-500"
          :name="getIcon('chevronDown')"
        />
      </div>
    </template>
  </UInputMenu>
</template>

<i18n lang="json">
{
  "en": {
    "noCompaniesFound": "No companies found",
    "placeholder": "Search a company"
  },
  "es": {
    "noCompaniesFound": "No se encontraron empresas",
    "placeholder": "Buscar una empresa"
  },
  "fr": {
    "noCompaniesFound": "Aucune entreprise trouvée",
    "placeholder": "Rechercher une entreprise"
  }
}
</i18n>
